// Copyright (c) 2026 Anchorbase, Inc. All rights reserved.

import {
    WINDOWS_INSTALLER_PUBLISHED_FILE_NAME,
    WINDOWS_INSTALLER_QA_PUBLISHED_FILE_NAME,
} from './windows-artifact-qa.types';

export const MACOS_INSTALLER_PUBLISHED_FILE_NAME = 'Anchorbase App.dmg';

export interface BuildReleaseIdentityV1 {
    readonly identityKind: 'build';
    readonly buildNumber: string;
}

export interface VersionReleaseIdentityV1 {
    readonly identityKind: 'release';
    readonly releaseVersion: string;
}

// Discriminator: identityKind
export type ReleaseIdentityV1 = BuildReleaseIdentityV1 | VersionReleaseIdentityV1;

interface NativeReleasePackageBaseV1 {
    readonly objectKey: string;
    readonly sha256: string;
}

export interface MacArm64DmgPackageV1 extends NativeReleasePackageBaseV1 {
    readonly packageKind: 'mac_arm64_dmg';
    readonly platform: 'mac';
    readonly architecture: 'arm64';
    readonly packageType: 'dmg';
    readonly publicFileName: typeof MACOS_INSTALLER_PUBLISHED_FILE_NAME;
}

export interface MacX64DmgPackageV2 extends NativeReleasePackageBaseV1 {
    readonly packageKind: 'mac_x64_dmg';
    readonly platform: 'mac';
    readonly architecture: 'x64';
    readonly packageType: 'dmg';
    readonly publicFileName: typeof MACOS_INSTALLER_PUBLISHED_FILE_NAME;
}

interface WindowsReleasePackageBaseV1 extends NativeReleasePackageBaseV1 {
    readonly platform: 'win';
    readonly architecture: 'x64' | 'arm64';
    readonly track: 'stable' | 'latest';
    readonly pipeline: 'stable';
    readonly imageLabel: string;
    readonly displayLabel: string;
}

export interface WindowsPortablePackageV1 extends WindowsReleasePackageBaseV1 {
    readonly packageKind: 'windows_portable';
    readonly packageType: 'portable';
    readonly publicFileName: string;
}

export interface WindowsInstallerPackageV1 extends WindowsReleasePackageBaseV1 {
    readonly packageKind: 'windows_installer';
    readonly packageType: 'installer';
    readonly publicFileName: typeof WINDOWS_INSTALLER_PUBLISHED_FILE_NAME;
    readonly qa: {
        readonly objectKey: string;
        readonly sha256: string;
        readonly publicFileName: typeof WINDOWS_INSTALLER_QA_PUBLISHED_FILE_NAME;
    };
}

// Discriminator: packageKind
export type NativeReleasePackageV1 =
    | MacArm64DmgPackageV1
    | WindowsPortablePackageV1
    | WindowsInstallerPackageV1;

export type NativeReleasePackageV2 = NativeReleasePackageV1 | MacX64DmgPackageV2;

export interface NativeReleaseCatalogV1 {
    readonly schemaVersion: 1;
    readonly portal: {
        readonly identity: ReleaseIdentityV1;
        readonly imageTag: string;
        readonly sourceCommit: string;
    };
    readonly native: {
        readonly identity: ReleaseIdentityV1;
        readonly artifactTag: string;
        readonly sourceGitTag: string;
        readonly sourceCommit: string;
        readonly nativeInputManifestSha256: string;
        readonly latestNativeInputCommit: string;
        readonly nativeFingerprint: string;
    };
    readonly packages: readonly NativeReleasePackageV1[];
}

export interface NativeReleaseCatalogV2 {
    readonly schemaVersion: 2;
    readonly portal: NativeReleaseCatalogV1['portal'];
    readonly native: NativeReleaseCatalogV1['native'];
    readonly packages: readonly NativeReleasePackageV2[];
}

export type NativeReleaseCatalog = NativeReleaseCatalogV2;
